import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { ThemeWrapper } from "@flixbus/honeycomb-react";

export type ThemeContextModel = {
  isDark: boolean;
  toggleDark: () => void;
};

export const ThemeContext = React.createContext<ThemeContextModel | undefined>(
  undefined,
);
ThemeContext.displayName = "ThemeContext";

const localStorageKey = "network-bdts-user-theme";

const getInitialDark = (): boolean => {
  try {
    return localStorage.getItem(localStorageKey) === "dark";
  } catch {
    return false;
  }
};

const ThemeProviderComp: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(getInitialDark);

  const toggleDark = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(localStorageKey, next ? "dark" : "light");
      } catch {
        console.warn("Failed to save theme in localStorage");
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ isDark, toggleDark }), [isDark, toggleDark]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeWrapper dark={isDark} className="full-height" applyToRoot>
        {children}
      </ThemeWrapper>
    </ThemeContext.Provider>
  );
};

export const ThemeProvider = React.memo(ThemeProviderComp);

export const useThemeContext = (): ThemeContextModel => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within ThemeProvider");
  }
  return context;
};
